"use client";
import { useState, useEffect } from 'react';

interface Testimonial {
  _id: string;
  title: string;
  content: string;
  rating: number;
  userId: {
    name: string;
  };
  category: string;
  createdAt: string;
}

export default function TestimonialsSection() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/api/testimonials/public?limit=6`);
      if (response.ok) {
        const data = await response.json();
        setTestimonials(data);
      }
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating: number) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  if (loading) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What Our Users Say</h2>
            <div className="animate-pulse">Loading testimonials...</div>
          </div>
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) {
    return null; // Don't show section if no testimonials
  }

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">What Our Users Say</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Real experiences from real users who have found success on our platform
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial._id}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 text-lg">
                  {renderStars(testimonial.rating)}
                </div>
                <span className="ml-2 text-sm text-gray-500">
                  {testimonial.rating}/5
                </span>
              </div>

              <h3 className="font-semibold text-gray-900 mb-2">
                {testimonial.title}
              </h3>

              <p className="text-gray-600 mb-4 line-clamp-3">
                "{testimonial.content}"
              </p>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">
                    {testimonial.userId.name}
                  </p>
                  <p className="text-sm text-gray-500 capitalize">
                    {testimonial.category.replace('_', ' ')}
                  </p>
                </div>
                <div className="text-xs text-gray-400">
                  {new Date(testimonial.createdAt).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <button className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition">
            View All Testimonials
          </button>
        </div>
      </div>
    </section>
  );
}